import { Input, InputNumber, Select, Space, Switch } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import type { ColumnsType } from 'antd/es/table/interface';
import { localize } from 'mixins/helpers';
import React from 'react';
import { ApiAccessRecord } from '/react4xp/parts/admin-api-access/types';
import { DEFAULT_PAGE_SIZE } from '../utils/constants';
import { getNumberOrNullFromInput } from '../utils/helpers';

interface GetApiAccessColumnsParams {
    disabled: boolean;
    pagination: TablePaginationConfig;
    translations: Record<string, string>;
    onAllowedChange: (apiCode: string, isAllowed: boolean) => void;
    onLimitChange: (apiCode: string, limitPerDay: number | null) => void;
    onMinuteLimitChange: (apiCode: string, limitPerMinute: number | null) => void;
}

export const getApiAccessColumns = ({
    disabled,
    onAllowedChange,
    onLimitChange,
    onMinuteLimitChange,
    pagination,
    translations,
}: GetApiAccessColumnsParams): ColumnsType<ApiAccessRecord> => [
    {
        title: localize('admin-api-access-column-number', translations),
        key: 'number',
        width: 60,
        align: 'center',
        render: (_value, _record, index) =>
            ((pagination.current || 1) - 1) *
                (pagination.pageSize || DEFAULT_PAGE_SIZE) +
            index +
            1,
    },
    {
        title: localize('admin-api-access-column-api', translations),
        dataIndex: 'apiName',
        key: 'apiName',
        width: 320,
        render: (value: string) => value || '-',
    },
    {
        title: localize('admin-api-access-column-api-code', translations),
        dataIndex: 'apiCode',
        key: 'apiCode',
        width: 180,
        align: 'center',
    },
    {
        title: localize('admin-api-access-column-allowed', translations),
        dataIndex: 'isAllowed',
        key: 'isAllowed',
        width: 140,
        align: 'center',
        render: (isAllowed: boolean, record) => (
            <Switch
                checked={isAllowed}
                disabled={disabled}
                onChange={(checked) =>
                    onAllowedChange(record.apiCode, checked)
                }
            />
        ),
    },
    {
        title: localize(
            'admin-api-access-column-limit-per-day',
            translations
        ),
        dataIndex: 'limitPerDay',
        key: 'limitPerDay',
        width: 180,
        align: 'center',
        render: (limitPerDay: number | null, record) => (
            <Input
                disabled={disabled || !record.isAllowed}
                value={limitPerDay === null ? '' : String(limitPerDay)}
                placeholder={localize(
                    'admin-api-access-no-limit',
                    translations
                )}
                inputMode="numeric"
                pattern="[0-9]*"
                style={{
                    width: 120,
                    textAlign: 'center',
                }}
                onChange={(event) =>
                    onLimitChange(
                        record.apiCode,
                        getNumberOrNullFromInput(event.target.value)
                    )
                }
            />
        ),
    },
    {
        title: localize(
            'admin-api-access-column-limit-per-minute',
            translations
        ),
        dataIndex: 'limitPerMinute',
        key: 'limitPerMinute',
        width: 300,
        align: 'center',
        render: (limitPerMinute: number | null, record) => {
            const mode =
                limitPerMinute === null
                    ? 'default'
                    : limitPerMinute === -1
                        ? 'unlimited'
                        : 'custom';

            return (
                <Space>
                    <Select
                        disabled={disabled || !record.isAllowed}
                        value={mode}
                        style={{ width: 150 }}
                        onChange={(value: string) => {
                            if (value === 'default') {
                                onMinuteLimitChange(record.apiCode, null);
                                return;
                            }

                            if (value === 'unlimited') {
                                onMinuteLimitChange(record.apiCode, -1);
                                return;
                            }

                            onMinuteLimitChange(
                                record.apiCode,
                                limitPerMinute !== null &&
                                limitPerMinute >= 0
                                    ? limitPerMinute
                                    : 0
                            );
                        }}
                    >
                        <Select.Option value="default">
                            {localize(
                                'admin-api-access-minute-limit-default',
                                translations
                            )}
                        </Select.Option>

                        <Select.Option value="unlimited">
                            {localize(
                                'admin-api-access-minute-limit-unlimited',
                                translations
                            )}
                        </Select.Option>

                        <Select.Option value="custom">
                            {localize(
                                'admin-api-access-minute-limit-custom',
                                translations
                            )}
                        </Select.Option>
                    </Select>

                    {mode === 'custom' && (
                        <InputNumber
                            disabled={disabled || !record.isAllowed}
                            min={0}
                            precision={0}
                            value={limitPerMinute}
                            style={{
                                width: 100,
                            }}
                            onChange={(value) =>
                                onMinuteLimitChange(
                                    record.apiCode,
                                    value === null
                                        ? 0
                                        : Number(value)
                                )
                            }
                        />
                    )}
                </Space>
            );
        },
    },
];